import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  createLiveStream,
  startLiveStream,
  endLiveStream,
  watchLiveStream,
} from '../services/liveStreamService';
import { LiveStream as LiveStreamType } from '../types/video';
import './Pages.css';

export const LiveStream: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const streamId = searchParams.get('streamId');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isLive, setIsLive] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStreamId, setCurrentStreamId] = useState<string | null>(streamId);
  const [stream, setStream] = useState<LiveStreamType | null>(null);

  useEffect(() => {
    if (currentStreamId) {
      const unsubscribe = watchLiveStream(currentStreamId, (updatedStream) => {
        if (updatedStream) {
          setStream(updatedStream);
          setViewerCount(updatedStream.viewerCount || 0);
          if (updatedStream.status === 'live') {
            setIsLive(true);
          }
          if (updatedStream.status === 'ended') {
            alert('A live foi encerrada.');
            navigate(-1);
          }
        }
      });
      return unsubscribe;
    }
  }, [currentStreamId]);

  const handleCreateLive = async () => {
    if (!title.trim()) {
      alert('Por favor, insira um título para a live.');
      return;
    }

    const userRole = (user as any)?.role || 'patient';
    if (userRole !== 'nutritionist') {
      alert('Apenas nutricionistas podem criar lives.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!user?.uid) {
        throw new Error('Usuário não autenticado');
      }

      const newStream = await createLiveStream(
        user.uid,
        title.trim(),
        description.trim() || undefined
      );
      setCurrentStreamId(newStream.id);
      setStream(newStream);
      setLoading(false);
    } catch (error: any) {
      console.error('Erro ao criar live:', error);
      setError(error.message || 'Erro ao criar live');
      setLoading(false);
    }
  };

  const handleStartBroadcast = async () => {
    if (!currentStreamId || !user?.uid) return;
    try {
      setLoading(true);
      await startLiveStream(currentStreamId, user.uid);
      setIsLive(true);
      setLoading(false);
    } catch (error: any) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleEndLive = async () => {
    if (window.confirm('Deseja encerrar a live?')) {
      try {
        if (currentStreamId) {
          await endLiveStream(currentStreamId);
        }
        navigate(-1);
      } catch (error) {
        console.error('Erro ao encerrar live:', error);
      }
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copiado para a área de transferência');
  };

  if (currentStreamId && stream) {
    return (
      <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ margin: 0 }}>{stream.title}</h1>
          <div style={{
            padding: '6px 12px',
            borderRadius: '16px',
            background: isLive ? '#4caf50' : '#ff9800',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            {isLive ? 'AO VIVO' : 'AGUARDANDO'}
          </div>
        </div>

        <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
          <h2 style={{ marginTop: 0 }}>Dados de Transmissão (RTMP)</h2>
          <p style={{ color: '#666', marginBottom: '24px' }}>
            Use estes dados no seu software de streaming (OBS, Larix, etc) para iniciar a transmissão.
          </p>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>Servidor RTMP:</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={stream.rtmpUrl || ''}
                readOnly
                style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd', background: '#f5f5f5' }}
              />
              <button onClick={() => copyToClipboard(stream.rtmpUrl || '')} style={{ padding: '8px 16px', cursor: 'pointer' }}>Copiar</button>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>Chave de Transmissão (Stream Key):</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="password"
                value={stream.muxStreamKey || ''}
                readOnly
                style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd', background: '#f5f5f5' }}
              />
              <button onClick={() => copyToClipboard(stream.muxStreamKey || '')} style={{ padding: '8px 16px', cursor: 'pointer' }}>Copiar</button>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
          <div style={{ flex: 1, background: '#fff', padding: '16px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196f3' }}>{viewerCount}</div>
            <div style={{ color: '#666' }}>Espectadores</div>
          </div>
          <div style={{ flex: 1, background: '#fff', padding: '16px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196f3' }}>
              {isLive ? 'Ao Vivo' : '00:00'}
            </div>
            <div style={{ color: '#666' }}>Duração</div>
          </div>
        </div>

        {!isLive ? (
          <button
            onClick={handleStartBroadcast}
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              background: '#2196f3',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            {loading ? 'Processando...' : 'Confirmar Início da Transmissão'}
          </button>
        ) : (
          <button
            onClick={handleEndLive}
            style={{
              width: '100%',
              padding: '16px',
              background: '#f44336',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Encerrar Transmissão
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '24px' }}>Criar Live</h1>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Título da Live *
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex: Dicas de Nutrição"
          style={{
            width: '100%',
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '16px',
          }}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Descrição (opcional)
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descreva o conteúdo da live..."
          rows={4}
          style={{
            width: '100%',
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '16px',
            resize: 'vertical',
          }}
        />
      </div>

      {error && (
        <div style={{ padding: '12px', background: '#ffebee', borderRadius: '8px', marginBottom: '16px', color: '#c62828' }}>
          {error}
        </div>
      )}

      <button
        onClick={handleCreateLive}
        disabled={loading || !title.trim()}
        style={{
          width: '100%',
          padding: '16px',
          background: loading || !title.trim() ? '#ccc' : '#4caf50',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: loading || !title.trim() ? 'not-allowed' : 'pointer',
        }}
      >
        {loading ? 'Iniciando...' : 'Criar Live'}
      </button>
    </div>
  );
};
